import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import { whatsappLink, telLink } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/properties", label: "Properties" },
  { to: "/land-survey", label: "Land Survey" },
  { to: "/documentation", label: "Documentation" },
  { to: "/blog", label: "Knowledge" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    h();
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#101827]/95 backdrop-blur-xl border-b border-[#C5A46D]/20" : "bg-[#101827]/85 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="ae-container flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-gradient-to-br from-[#C5A46D] to-[#8E7340] flex items-center justify-center shadow-md">
            <span className="font-display font-bold text-[#101827] text-lg">A</span>
          </div>
          <div className="leading-tight">
            <div className="text-white font-bold tracking-wide text-sm md:text-base">APURBA ESTATE</div>
            <div className="text-[10px] md:text-[11px] text-[#C5A46D] tracking-[0.25em] uppercase">Real Estate · Survey · Advisory</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#C5A46D]" : "text-white/80 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={telLink()}
            data-testid="nav-call-button"
            className="w-10 h-10 flex items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-[#C5A46D] hover:border-[#C5A46D]/50 transition"
            aria-label="Call"
          >
            <Phone className="w-4 h-4" />
          </a>
          <a
            href={whatsappLink("Hello Apurba Estate, I would like to consult your team.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="ae-btn-gold !py-2.5 !px-4 text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            Consult Our Team
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden w-10 h-10 flex items-center justify-center text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#101827] border-t border-[#C5A46D]/20 ae-fade-up">
          <nav className="ae-container py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 border-b border-white/5 text-sm font-medium ${
                    isActive ? "text-[#C5A46D]" : "text-white/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 mt-4">
              <a href={telLink()} className="ae-btn-outline flex-1 justify-center !border-white/20 !text-white hover:!bg-white hover:!text-[#101827]">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a
                href={whatsappLink("Hello Apurba Estate, I would like to consult your team.")}
                target="_blank"
                rel="noopener noreferrer"
                className="ae-btn-gold flex-1 justify-center"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
